import { stationTimeStamps, WeightType } from '../models/knitting/roll';

function sortByTimeStampDesc(stamps: stationTimeStamps[]): stationTimeStamps[] {
  return [...stamps].sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime());
}

/**
 * Last processing weight stamp for a roll: the most recent QualityControl stamp, or if none
 * exists, the Production stamp. Excludes Final Weight and Adjustment/Split stamps, which use
 * neither classification — except when the roll has no Production/QualityControl stamp at all
 * (e.g. B-Grade or migrated rolls created directly with an adjustment stamp), in which case we
 * fall back to the last stamp overall rather than reporting no weight.
 */
export function getLastProcessingStamp(stamps: stationTimeStamps[] | undefined): stationTimeStamps | undefined {
  if (!stamps || stamps.length === 0) {
    return undefined;
  }

  const lastQualityControl = sortByTimeStampDesc(stamps.filter((s) => s.weightType === WeightType.QualityControl))[0];
  if (lastQualityControl) {
    return lastQualityControl;
  }

  const production = stamps.find((s) => s.weightType === WeightType.Production);
  if (production) {
    return production;
  }

  return sortByTimeStampDesc(stamps)[0];
}

/**
 * The most recent stamp for a roll of ANY type (Production, QualityControl, ParentSplit,
 * ChildSplit, Adjustment) ordered by timestamp — unlike getLastProcessingStamp, this does not
 * filter by WeightType. Mirrors the fallback used by the backend's RollWeightHelper for
 * RollWeightType.CurrentWeight (FinalWeight if set, else the last stamp overall). Use this
 * (via KnittingUtil.getCurrentWeightInLbs) when the UI needs "what does this roll weigh right
 * now" — e.g. after a split or a Final Weight registration — rather than getLastProcessingStamp,
 * which intentionally excludes Final Weight and Split stamps for contexts that want the
 * pre-final/pre-split weight specifically (Bug 5097).
 */
export function getMostRecentStamp(stamps: stationTimeStamps[] | undefined): stationTimeStamps | undefined {
  if (!stamps || stamps.length === 0) {
    return undefined;
  }

  return sortByTimeStampDesc(stamps)[0];
}
